import { ArrowButton } from "@/components/mypage/arrowButton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuthStore } from "@/store/auth.store";
import { ThumbaTrophyIcon } from "@roman/shared/components/Icons";
import type React from "react";
import { useState } from "react";
import type { CardQuantity, MethodContent } from "../..";
import { Icon } from "../../PaymentMethod/Icon";
import { calculateTotalYen, calculateTotalYenWithCommission } from "../../utils";

export enum PaymentIconSrc {
  Amazon = "/assets/amazon-gift.svg",
  Google = "/assets/google-gift.svg",
  Apple = "/assets/apple-gift.svg",
  AuPay = "/assets/aupay-confirmation.svg",
  Giftee = "/assets/gifteeboxconfirm.png",
}

type WithdrawalConfirmationPageProps = {
  methodContent: MethodContent;
  cardQuantities: CardQuantity[];
  onClick: () => void;
  backButtonClick: () => void;
};

export const WithdrawalConfirmationPage: React.FC<WithdrawalConfirmationPageProps> = ({
  methodContent,
  cardQuantities,
  onClick,
  backButtonClick,
}) => {
  const user = useAuthStore((state) => state.user);
  if (!user) {
    throw new Error("User not found");
  }
  const totalYen = calculateTotalYenWithCommission(cardQuantities);
  const [open, setOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOpen(true);
  };

  return (
    <div className="bg-white min-w-full min-h-screen">
      <form className="p-[18px] flex flex-col items-start gap-10" onSubmit={handleSubmit}>
        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center gap-3">
            <ArrowButton className="-rotate-90" onClick={backButtonClick} />
            <p className="text-xl font-bold -tracking-[.01em] text-black">出金の最終確認</p>
          </div>
          <div className="flex flex-col items-start gap-1">
            <p className="text-lg text-black font-bold -tracking-[.01em]">注意</p>
            <p className="text-[10px] font-normal -tracking-[.01em] text-gray">
              ※交換後に交換商品のキャンセル・変更はできないため、交換前にご確認をお願いします。
            </p>
            <p className="text-[10px] font-normal -tracking-[.01em] text-gray">
              ※出金申請中は、同時に複数の申請を行うことはできませんので、あらかじめご了承ください。
            </p>
          </div>
        </div>
        <div className="flex flex-col items-start gap-3 w-full">
          <div className="flex items-center">
            <p className="text-xl font-bold -tracking-[.01em] text-black">メールアドレス</p>
          </div>
          <div className="flex flex-col items-start gap-3 w-full">
            <p className="text-base w-full font-normal h-[19px] pb-[2rem] border-b border-disabled border-solid outline-none">
              {user.googleEmail}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-start gap-1 w-full">
          <p className="text-lg text-black font-bold -tracking-[.01em]">交換する商品</p>
          <div className="pb-1 border-b border-[#ECECEC] border-solid w-full flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-start gap-2">
                <div className="h-[50px] w-[50px]">
                  <Icon
                    className="w-full h-full"
                    src={PaymentIconSrc[methodContent.method as keyof typeof PaymentIconSrc]}
                    imageSize={{ width: "54", height: "50" }}
                  />
                </div>
                <p className="text-lg font-bold">{methodContent.heading}</p>
                <p className="text-base font-bold">{calculateTotalYen(cardQuantities).toLocaleString()}円分</p>
              </div>
              <div className="flex flex-col items-end w-full gap-1">
                {cardQuantities.map(({ yenOption, quantity }) => (
                  <p key={yenOption} className="text-primary text-base font-bold">
                    {Number(yenOption).toLocaleString()}円分 X {quantity}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start gap-1 w-full">
          <p className="text-lg text-black font-bold -tracking-[.01em]">合計消費円分</p>
          <div className="pb-1 border-b border-[#ECECEC] border-solid w-full flex items-center justify-end gap-1">
            <div className="h-6 w-6">
              <ThumbaTrophyIcon />
            </div>
            <div>
              <p className="text-base font-bold text-black">{totalYen.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start gap-1 w-full">
          <p className="text-[10px] font-normal -tracking-[.01em] text-gray">
            間違った情報を入力すると、取引が拒否され、あなたのリクエストの処理に関連するすべての消費をあなたが負担することになります。
          </p>
        </div>

        <button
          className="flex items-center justify-center bg-primary text-white py-2.5 px-4 text-lg font-bold w-full rounded-md"
          type="submit"
        >
          確認
        </button>
      </form>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="[&>button]:hidden py-8 w-[350px] md:w-[450px] h-[383px] ring-0 ring-transparent">
          <DialogTitle className="dialog-content text-black text-center font-Inter text-lg font-semibold leading-[30px] max-w-[287px] mx-auto">
            <p>本当に出金してもよろしいですか？</p>
          </DialogTitle>

          <div className="flex justify-center items-center gap-1 bg-gray rounded-full text-black text-lg w-fit mx-auto px-4 py-[5px] font-bold leading-[18px]">
            <ThumbaTrophyIcon className="h-6 w-6 space-x-2" />
            <span className="align-middle leading-[18px]">{totalYen.toLocaleString()}</span>
          </div>
          <div className="text-dark-gray text-center text-base font-bold flex items-center justify-center min-h-[30px]">
            <span>確定後、キャンセルはできません</span>
          </div>

          <div className="flex flex-col w-full items-center justify-center mt-1">
            <Button
              className="text-lg w-full py-[25px] text-white rounded-lg font-bold"
              variant="default"
              onClick={() => {
                setOpen(false);
                onClick();
              }}
            >
              出金を確定する
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
